import { useEffect, useRef, useState } from "react";
import { SpeakerHigh, StopCircle, CircleNotch } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function ListenButton({ text, label = "Listen", testid, className = "" }) {
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const audioRef = useRef(null);
  const partsRef = useRef([]);
  const idxRef = useRef(0);

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
    setState("idle");
  };

  useEffect(() => stop, []); // eslint-disable-line

  const playPart = () => {
    const src = partsRef.current[idxRef.current];
    if (!src) { setState("idle"); return; }
    const a = new Audio(`${BASE}${src}`);
    audioRef.current = a;
    a.onended = () => { idxRef.current += 1; playPart(); };
    a.onerror = () => setState("idle");
    a.play().then(() => setState("playing")).catch(() => setState("idle"));
  };

  const onClick = async () => {
    if (state === "playing") {
      audioRef.current?.pause();
      setState("paused");
      return;
    }
    if (state === "paused") {
      audioRef.current?.play();
      setState("playing");
      return;
    }
    setState("loading");
    try {
      const res = await fetch(`${BASE}/api/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not read this section aloud");
      partsRef.current = data.parts || [];
      idxRef.current = 0;
      playPart();
    } catch (e) {
      setState("idle");
      alert(e.message);
    }
  };

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <button type="button" onClick={onClick}
        className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white/90 px-2.5 py-1 text-[11px] font-medium text-stone-600 shadow-sm transition-colors hover:bg-stone-900 hover:text-white"
        data-testid={testid || "listen-button"}>
        {state === "loading" ? <CircleNotch size={12} className="animate-spin" /> : state === "idle" ? <SpeakerHigh size={12} /> : <SpeakerHigh size={12} weight="fill" className="text-primary" />}
        {state === "loading" ? "Preparing…" : state === "playing" ? "Pause" : state === "paused" ? "Resume" : label}
      </button>
      {(state === "playing" || state === "paused") && (
        <button type="button" onClick={stop} className="text-stone-400 hover:text-[#C0392B]" data-testid="listen-stop" title="Stop">
          <StopCircle size={16} />
        </button>
      )}
    </span>
  );
}
